import React, { useEffect, useRef, useState } from 'react';
import { useMessage } from '../../hooks/useMessage';
import { FieldDef, FormsProps, FormsResultsDTO } from '../../types/forms';
import { deleteFormResponse, getFormsRows } from '../../api/forms.api';
import Loading from '../loading/Loading';
import { Typography } from '@mui/material';
import FormsResultsTable from '../FormsResultsTable/FormsResultsTable';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { transformFormResponses } from '../../utils/forms.utils';
import { useEvents } from '../../hooks/useEvents';
import { useError } from '../../context/error-context';

const ViewTableForm = ({ formId, formAllFields, linkedFormId, skin, formName }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ results, setResults ] = useState<FormsResultsDTO['responses']>([]);
    const [ resultsLoading, setResultsLoading ] = useState(true);
    const [ confirmModalOpen, setConfirmModalOpen ] = useState(false);
    const [ removeLoading, setRemoveLoading ] = useState(false);
    const responseIdsToRemove = useRef<(string | number)[]>([]);
    const { subscribe } = useEvents();
    const { showError } = useError();

    let realFormId = +formId;

    if (linkedFormId) {
        realFormId = linkedFormId;
    }

    useEffect(() => {
        setResultsLoading(true);

        getFormsRows(realFormId, formId)
        .then(response => {
            setResults(response.responses);
        })
        .catch((err) => {
            showError({
                code: err.response?.status || 500,
                message: 'API Error',
                description: 'Failed to fetch data',
                showRefresh: true,
            });
        })
        .finally(() => setResultsLoading(false));
    }, [ realFormId, formId, showError ]);

    useEffect(() => {
        return subscribe(`REFRESH_FORMS_${realFormId}`, () => {
            setResultsLoading(true);

            getFormsRows(realFormId, formId)
            .then(response => {
                setResults(response.responses);
            })
            .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to fetch data',
                    showRefresh: true,
                });
            })
            .finally(() => setResultsLoading(false));
        });
    }, [ realFormId, formId, subscribe, showError ]);

    const onDeleteConfirm = async () => {
        if (responseIdsToRemove.current.length === 0 || !realFormId) {
            return;
        }

        setRemoveLoading(true);

        Promise.all(
            responseIdsToRemove.current.map((id) => deleteFormResponse(realFormId, id)),
        )
            .then(() => {
                showMessage('The response(s) has been deleted.', 'success');

                const filteredResults = results.filter(item => !responseIdsToRemove.current.includes(item.response_id));

                setResults(filteredResults);
                responseIdsToRemove.current = [];
            })
            .catch(() => {
                showMessage('Couldn\'t remove the record(s). Please try again later.', 'error');
            })
            .finally(() => {
                setConfirmModalOpen(false);
                setRemoveLoading(false);
            });
    };

    const shouldShowColumn = (field: FieldDef): boolean => {
        if (skin === 'skin3' && field.type === 'string') {
            return false;
        }

        return !field.hidden;
    };

    const finalFormName = formName || 'Submitted Results';

    return (
        <div>
            {resultsLoading ? <Loading/> : (
                <div style={{ margin: '16px auto 0', width: '98%' }}>
                    <div>
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={skin === 'skin5' ? {
                                color: '#22312d',
                                fontFamily: "Georgia, 'Times New Roman', serif",
                                fontWeight: 700,
                            } : undefined}
                        >
                            {finalFormName}
                        </Typography>
                    </div>

                    {formAllFields.length > 0 ? (
                        <FormsResultsTable
                            config={{ fields: formAllFields.filter(shouldShowColumn) }}
                            formName={finalFormName}
                            data={transformFormResponses(results)}
                            isSearchable
                            isSortable
                            skin={skin}
                        />
                    ) : (
                        <Typography variant="h6">
                            Couldn't load the results.
                        </Typography>
                    )}
                </div>
            )}

            <ConfirmModal
                isLoading={removeLoading}
                open={confirmModalOpen}
                title="Are you sure?"
                message="You will not be able to restore the removed data!"
                onConfirm={onDeleteConfirm}
                onCancel={() => setConfirmModalOpen(false)}
            />

            <MessageContainer/>
        </div>
    );

    };

export default ViewTableForm;